export const blogPostsDetail = {
  1: {
    id: 1,
    title: "Getting Started with 42 School: A Student's Experience",
    excerpt:
      "My journey through the 42 School program, the peer led learning model, and how it shaped my approach to coding and problem-solving.",
    content: `When I first heard about 42 School, no teachers, no lectures, no tuition. I assumed it was too good to be true. No grades? You learn by doing projects alongside other students? It sounded chaotic. But after going through the infamous Piscine (a month long coding bootcamp selection process) and becoming a full cadet, I quickly realized: the chaos is by design. And it works.

## The Piscine: Learning to Swim by Being Thrown in the Deep End

The Piscine was four weeks of controlled intensity. Every morning at 8:42 AM, a new project dropped. No slides, no professors just a subject PDF and a terminal. I remember staring at my first C exercise, ft_putchar, thinking: "How do I even compile this?"

But that's the point. 42 forces you to learn how to learn. Within days, I went from copy pasting syntax to reading man pages, debugging with valgrind, and asking peers for help without feeling embarrassed. The peer evaluation system (where you grade each other's code) was terrifying at first but it taught me more than any automated test ever could. You learn to justify your design choices, spot edge cases, and accept feedback gracefully.

## The Peer Led Model: No Heroes, Only Collaborators

Traditional group projects often devolve into one person doing the work. At 42, collaboration is mandatory but not in a forced way. You work side by side with someone on the same terminal (mob programming), explain your logic out loud, and catch each other's blind spots.

I remember working on get_next_line with a classmate who spotted a memory leak I'd missed for two days. A week later, I helped them understand how static variables persist across function calls. That's the unwritten rule: you give what you get. The result? I stopped seeing coding as a solitary grind and started treating it as a team sport.

## Problem Solving Without a Safety Net

At a traditional university, if you're stuck, you raise your hand. At 42, there's no hand to raise. You have:

- The man pages
- Google (yes, we use it)
- Your peers
- Your own persistence

This forces a different kind of resilience. When I hit a segmentation fault on minishell (building a simple shell from scratch), I couldn't ask a professor for the answer. I had to trace the bug, isolate it, and reason through it. It took longer, but the moment I fixed it? I owned that solution. That feeling never gets old.

## How It Shaped Me as a Programmer

I'm no longer afraid of the unknown. Give me a new language, framework, or tool. I know I can pick it up by reading docs and building something small.

I write code for humans, not just machines. Peer reviews enforce clean, readable, and well documented code. No "clever" one liners without comments.

I debug with logic, not guesswork. Valgrind, fsanitize, and step by step reasoning are my reflex now.

I value feedback over praise. A harsh peer evaluation is worth more than an easy A. It's how I grow.

## Advice for New 42 Students

If you're about to start or just finished the Piscine:

1. **Don't hoard knowledge.** Explain a concept to someone else it solidifies your own understanding.
2. **Read the subject three times.** 42 subjects are intentionally dense. Missing one line can cost you days.
3. **Fail fast and publicly.** The best coders in my cohort are the ones who ask "stupid questions" in the common area.
4. **Your norminette style matters.** It feels petty at first. Later, you'll appreciate consistent formatting when reviewing 500 line files.

## Final Thought

42 didn't just teach me C, shell scripting, or network programming. It taught me how to teach myself. In an industry where tools change every 18 months, that's the only skill that truly lasts.

I'm still a student—there are plenty of projects left, bugs to chase, and concepts to master. But I've already gotten the most valuable thing from 42: the confidence to sit down, open a terminal, and figure it out.`,
    date: "2024-01-15",
  },
  2: {
    id: 2,
    title: "Debugging Tips from the 42 Core Curriculum",
    excerpt:
      "Essential debugging techniques and tools I've learned at 42 School. From GDB to memory debugging, strategies that work.",
    content: `Let me be honest: before 42, my debugging strategy was "add console.log statements until it works." Sometimes I'd get lucky. Most times, I'd waste hours moving print statements around like a person rearranging deck chairs on the Titanic.

Then came malloc, free, and segmentation faults that made no sense. Then came threads. Then came a custom HTTP server. 42 doesn't teach debugging in a lecture it forces you to learn by giving you bugs so subtle that only methodical, tool assisted thinking will crack them.

Here are the techniques and tools that saved my grade (and my sanity).

## 1. Valgrind: Your Memory's Best Friend

The first thing any 42 student learns: valgrind is not optional. It's the difference between "it works on my machine" and "it actually works."

Basic usage that catches 90% of memory bugs:

valgrind --leak-check=full --show-leak-kinds=all ./your_program

What it catches:

- Memory leaks (forgetting to free)
- Invalid reads/writes (accessing freed or out-of-bounds memory)
- Double frees
- Use of uninitialized values

Pro tip from libft hell: Run valgrind with --track-origins=yes when you get "conditional jump depends on uninitialized value." It tells you exactly where the uninitialized value came from. This alone saved me days of head scratching.

## 2. GDB: More Than Just "bt"

I used to think GDB was only for backtraces after a crash. Then I had to debug a minishell that would segfault only when the 42nd command was entered. Good luck with print statements there.

Essential GDB commands I now use daily:

- break file.c:42 - Set breakpoint at line 42
- run < test_input.txt - Run with redirected input
- print *ptr - Dereference a pointer
- watch var - Stop when 'var' changes value
- frame 3 - Jump to stack frame 3
- info locals - Show all local variables
- display var - Auto print 'var' every stop

The trick that changed everything: Conditional breakpoints. Instead of stopping 1000 times:

break my_function if counter == 42

For philosophers, I set a breakpoint that triggered only when a philosopher was about to die. Suddenly, the deadlock became obvious.

## 3. fsanitize: The Modern Alternative

Sometimes Valgrind is too slow (especially for cub3d or fdf with graphics). Enter the sanitizers:

gcc -g -fsanitize=address -fsanitize=undefined your_file.c

AddressSanitizer catches buffer overflows and use after free at runtime with exact line numbers. UndefinedBehaviorSanitizer catches things like signed integer overflow that Valgrind misses.

The output is verbose but readable. I keep a terminal tab just for sanitizer builds now.

## 4. The "Divide and Conquer" Method

When I faced a 500 line function in push_swap that sorted incorrectly, I didn't debug the whole thing. I did this instead:

Binary search on execution. Comment out half the function. Does it still fail? Yes? Bug is in first half. No? Bug is in second half.

Reduce input size. If it fails on 500 numbers, try 5. Then 3. Then 2. The minimal failing case reveals the bug pattern.

Print state at key points. Not everything just array length, first element, last element, and a checksum.

This turns a "I don't even know where to look" problem into a surgical strike.

## 5. The -g Flag Is Non-Negotiable

Always, always compile with -g during development:

gcc -Wall -Wextra -Werror -g your_code.c

Without debug symbols, GDB shows you memory addresses instead of variable names and line numbers. That's like trying to navigate a city with no street signs, only GPS coordinates.

Yes, your final submission might compile without -g. But while you're debugging? -g every time.

## 6. Logging That Doesn't Suck

Print statements aren't evil. Chaotic print statements are evil.

Bad:

printf("here\n");
printf("value: %d\n", x);

Good:

- #define DEBUG 1
- #ifdef DEBUG
- fprintf(stderr, "[%s:%d] my_function: x=%d, y=%d\n", __FILE__, __LINE__, x, y);
- #endif

Why stderr? Because it's unbuffered you see output immediately, even if your program crashes right after. The __FILE__ and __LINE__ macros tell you exactly where the print happened.

For get_next_line, I had a debug macro that printed buffer state before and after each read() call. The bug (off by one in buffer management) appeared in two minutes instead of two hours.

## 7. The "Rubber Duck" That Actually Works

42 has no professors to ask. But it has peers. When I'm stuck for more than 30 minutes, I find someone anyone and explain the bug out loud.

The script:

1. "Here's what I expect to happen..."
2. "Here's what actually happens..."
3. "Here's the last thing I tried..."
4. "Here's why I think that didn't work..."

Halfway through, I usually spot my own assumption error. The other half, my peer says "wait, did you check that pointer after the free?" and I feel both grateful and foolish.

## 8. Real Examples from 42 Projects

**libft (string functions):** My ft_strjoin leaked memory. Valgrind showed "definitely lost: 42 bytes." The fix? Realized I wasn't freeing the input string in the failure path.

**get_next_line (static buffer hell):** GDB with a conditional breakpoint on BUFFER_SIZE mismatch revealed that my static variable wasn't being reset on EOF. Added an explicit NULL check.

**push_swap (sorting algorithm):** Used fsanitize=address to catch a stack buffer overflow. I was writing to array[-1] when the pivot was the smallest element. Off-by-one in index bounds.

**philosophers (threading):** helgrind (Valgrind's thread checker) showed a data race on the "died" flag. Added a mutex. Problem solved.

**minishell (processes):** GDB with follow fork mode child let me debug the child process that was segfaulting on execve. The issue? Not null terminating the argument array.

## 9. The One Weird Trick: assert()

The assert() macro is underused. It stops your program immediately if a condition is false, with file and line number.

- #include <assert.h>
- assert(ptr != NULL);
- assert(index >= 0 && index < array_size);

I now sprinkle assertions at function boundaries. They act as executable documentation if my assumption is wrong, the program screams immediately, not 500 lines later.

For ft_printf, I asserted that the format string wasn't NULL before parsing. Caught a caller bug in three seconds.

## 10. The Mindset Shift

Before 42, I saw bugs as failures. Now I see them as information.

A segmentation fault isn't a punishment it's telling you exactly which memory access was illegal. A valgrind error isn't shame it's a map to a leak. A deadlock isn't wasted time it's a lesson about lock ordering.

The best debuggers I know at 42 aren't the ones who never write bugs. They're the ones who have a system:

- Run valgrind after every change
- Use GDB for anything that crashes
- Add assertions for assumptions
- Reduce the problem space
- Ask a peer when stuck for 30 minutes

## Final Thought

Debugging is 80% of programming. The other 20% is writing bugs worth debugging.

42 gave me the tools GDB, valgrind, sanitizers and more importantly, the patience to use them. I no longer guess. I no longer hope. I run the tools, read the output, and fix what's broken.

And when I finally see that "All heap blocks were freed no leaks are possible" message? It feels better than any grade ever could.`,
    date: "2024-01-18",
  },
  4: {
    id: 4,
    title: "Full Stack Development: From Backend to Frontend",
    excerpt:
      "Insights on building complete web solutions. Integrating databases, APIs, and frontend frameworks for seamless user experiences.",
    content: `After months of writing C at 42 managing memory, crafting linked lists, and wrestling with signals I assumed frontend work would be a relaxing vacation. You know, colors, buttons, maybe some console.log sprinkles. I was wrong. But I also discovered something surprising: my backend discipline made me a better frontend developer, and vice versa.

Here's what I've learned building complete web solutions, from database schema to deployed UI.

## The Backend: Where Logic Lives

My first real backend project was a REST API for a task manager. No framework just Node.js with vanilla HTTP modules. Why? Because I wanted to feel the pain. And I did.

Routing by hand taught me why Express (or FastAPI, or Spring) exists.

Parsing request bodies made me appreciate body parser middleware.

Setting CORS headers manually? Let's just say I now recite the "preflight request" mantra in my sleep.

But the real lesson came from 42's webserv project (building an HTTP server from scratch in C++). Suddenly, I understood what happens between "user clicks a button" and "JSON arrives in the browser." The event loop. Non blocking I/O. Socket states. That knowledge is invisible in high level frameworks but when something breaks, you know exactly where to look.

Takeaway: Build something low level at least once. It demystifies the entire stack.

## Databases: Schemas Are Like Data Structures (But Persistent)

After 42's libft and push_swap, I thought, "How hard can SQL be?" Then I tried to design a normalized schema for an e‑commerce site with product variants, user reviews, and order history.

Three hours later, I had five tables, two circular foreign key dependencies, and a quiet existential crisis.

What saved me? Thinking of databases the way 42 taught me to think about memory: plan before you allocate (or insert).

Normalization is like avoiding redundant pointers.

Indexes are like hash tables speed comes at a cost.

Transactions are like malloc/free group operations or roll back cleanly.

Now I use PostgreSQL or SQLite for small projects, and I always draw an ER diagram before writing a single CREATE TABLE. Future me thanks past me every time.

## The API Layer: The Uncomfortable Handshake

Connecting backend to frontend is where full stack theory meets reality. You write a clean endpoint in Go or Node, test it with curl, and it works perfectly. Then your React app tries to hit it, and you get:

- CORS errors (always)
- A malformed JSON body (sometimes)
- A date format your frontend can't parse (inevitably)

The solution wasn't more code it was agreement. I learned to:

Document API responses with TypeScript interfaces (even if the backend is JavaScript or Python).

Use consistent status codes (200 for success, 201 for creation, 400 for bad requests, 500 for "I'll debug this tomorrow").

Return error messages a human (or frontend) can actually act on.

Rule I now live by: Your API is a contract. Write it like a .h header file clear, strict, and kind to the person on the other side.

## Frontend: The UI Is a State Machine

My frontend journey started chaotic: useState here, a useEffect there, and suddenly two components were fighting over the same data. Then I remembered 42's philosophers project a multithreading simulation where every shared resource needs a clear owner.

Light bulb moment: frontend state is shared memory with a worse debugger.

Now I treat every UI as a state machine:

- Loading, success, error, empty explicit states.
- One source of truth (Context, Zustand, or just props drilled with intention).
- Side effects confined to well named functions (no anonymous useEffect blocks that do six things).

The result? Fewer "this button works but only after you click that other button first" bugs. And when bugs do happen, I trace them like a segmentation fault methodically, variable by variable.

## Bringing It All Together: A Full Stack Story

Last month I built a small habit tracker. Stack:

Backend: Node + Express + PostgreSQL (hosted on a tiny VPS)

Frontend: React + Tailwind (no build step, just CDN for simplicity)

Auth: HTTP only cookies (because localStorage tokens scared me after a security talk)

The moment it worked adding a habit from a form, seeing it appear in the database via psql, then watching it render in the browser felt like finishing a 42 project. Not because the code was brilliant, but because I understood every layer.

When the API returned a 500 error, I checked the logs.
When the UI froze, I looked at the React component lifecycle.
When the database was slow, I added an index.

No magic. No framework mystery. Just cause and effect, from the browser's fetch down to a disk write on a Linux server.

## What Full Stack Taught Me That 42 Couldn't

42 taught me to respect memory, concurrency, and algorithmic efficiency. Full stack work taught me:

- User experience matters. A perfectly optimized API is useless if the button is grayed out.
- End to end thinking. A bug might be in the database query, the API mapper, the frontend reducer, or the CSS. You have to be willing to look anywhere.
- Speed is a feature, not an afterthought. I learned to measure: time to first byte, time to interactive, bundle size. Users feel slowness before they read your elegant code.

## Advice for Fellow 42 Students Going Full Stack

Don't start with a framework. Build a simple HTTP server in Node or Python. Parse a form manually. Set headers. You'll appreciate Express later.

Learn SQL properly. ORMs are convenient until they aren't. Write joins by hand at least ten times.

Your frontend is not "less serious." State management, accessibility, and responsive layouts are hard problems. Respect them.

Deploy something real. A $5 VPS, a free Render or Fly.io tier just get your code running somewhere that isn't localhost.

## Final Thought

Full stack development isn't about knowing every framework. It's about moving confidently between layers from a database index to a button click without fear. My 42 background gave me the debugging mindset and the systems foundation. The web gave me the canvas.

And honestly? Watching a complete idea go from a database schema to a live, interactive page still feels like magic. Even when I know exactly how it works.`,
    date: "2024-01-20",
  },
  3: {
    id: 3,
    title: "Building Responsive Web Apps with React and Tailwind CSS",
    excerpt:
      "A guide to creating modern, responsive web applications. Best practices, component patterns, and performance optimization tips.",
    content: `When I started with React, I overcomplicated everything. Components were huge, state was everywhere, and my CSS was a mess of conflicting class names. Then I remembered something from 42: simple code is good code.

Here's the stripped down approach that finally worked for me.

## Why React + Tailwind?

React handles the moving parts what shows up, what changes when you click something.

Tailwind handles the looks spacing, colors, responsiveness without writing custom CSS files.

Together, they let me focus on building instead of fighting tooling.

## Think in Pieces, Not Pages

My first mistake: one giant component for everything. It worked until I needed to change something, and then everything broke.

The fix: Break your UI into small, reusable pieces.

- A Button component handles its own styling and click behavior.
- A Card component knows how to show an image, title, and description.
- A UserProfile component only cares about user data.

Each piece does one thing. If a file gets longer than 200 lines, I split it. Just like a 42 function shouldn't be 50 lines long.

## State: Don't Let Components Fight Over Data

State is just data that changes over time. The rules are simple:

- If only one component needs it → keep it inside that component.
- If multiple components need it → lift it up to a parent component.
- If it comes from a server (API) → treat it differently than UI state (like whether a menu is open).

The golden rule: one source of truth. If you store the same data in two places, they will get out of sync. You've been warned.

## Responsive Design Without the Headache

Tailwind makes responsive design almost fun. You don't write media queries. You just add prefixes:

- No prefix → mobile phones
- sm: → small tablets
- md: → desktops
- lg: → large screens

Example thinking: "On mobile, this image takes full width. On desktop, it sits next to the text."

That becomes: className="w-full lg:w-1/2"

Start with mobile styles first. Add larger screen styles on top. Test on an actual phone browser tools lie about how things feel to tap.

## Custom Hooks: Reusing Logic Without Copy Paste

A custom hook is just a function that uses React hooks internally. It lets you reuse logic across components.

Real example: A hook that saves user preferences to localStorage so they survive a page refresh. You write it once, use it in your theme toggler, sidebar settings, and notification preferences.

Same idea as writing a helper function in C don't repeat yourself.

## Performance: Don't Optimize Things That Aren't Slow

At 42, we optimized sorting algorithms because milliseconds mattered. In React, most performance problems come from unnecessary re-renders components redrawing when nothing changed.

Three simple fixes:

- React.memo — tells React "don't re‑render this component unless its props actually change."
- useCallback — keeps a function the same between renders (useful when passing functions down).
- useMemo — remembers the result of an expensive calculation.

But here's the secret: don't use any of them until you see a real slowdown. Profile with React DevTools first. Most apps are fast enough without optimization.

## Forms: Controlled vs. Uncontrolled

Controlled: React manages every keystroke. More code, but you can validate as the user types.

Uncontrolled: The browser manages the input. Less code, but you only get the value on submit.

Start uncontrolled with refs. Switch to controlled only when you need real‑time validation or complex dependencies between fields.

For big forms (10+ fields), use a library like react hook form. For small ones, plain React is fine.

## Talking to a Backend

You'll need to fetch data from your API. The simplest pattern:

- Show "Loading..." while waiting.
- If there's an error, show the error message.
- When data arrives, display it.

That's it. For most small projects, the built in fetch works fine. For bigger apps, look into TanStack Query it handles caching, retries, and background refetching automatically.

## Deployment: From Your Computer to the World

Once your app works locally, you need to put it online.

Easiest options:

- Vercel — run vercel --prod in your terminal. Free SSL, automatic deploys from Git.
- Netlify — drag your build folder into the browser window. Done.

Build your app first with npm run build. That creates a folder of optimized files. Upload that folder, not your development code.

## Common Mistakes (And How to Avoid Them)

Mistake: Changing state directly → React doesn't know to re‑render → Always use setState

Mistake: Forgetting dependency arrays → useEffect runs too often or too little → Add all variables you use inside

Mistake: Not handling loading/error states → App crashes or shows nothing → Always show something (loading, error, or data)

Mistake: Huge components → Hard to debug, hard to change → Break into smaller pieces

## A Simple Mental Model

Think of React like this:

- State = memory that changes
- Props = arguments passed to a component
- Effects = things that happen after render (like fetching data)
- JSX = what the user sees (HTML looking stuff)

Tailwind is just shortcuts for CSS. p-4 = padding, m-2 = margin, flex = display flex, text-center = center text.

## Final Thought

You don't need to know every React hook or every Tailwind class. You need to know enough to build something that works, then look up the rest as you go.

The 42 mindset break problems down, test small pieces, and learn by doing works perfectly here. The tools are friendlier, but the logic is the same.

And when your app works on your phone, your laptop, and your friend's giant monitor? That feeling never gets old.

One Page Cheatsheet:

What you want: Reusable UI → React: Component (function MyButton()) → Tailwind: Utility classes (bg-blue-500 p-2 rounded)

What you want: Changing data → React: useState → Tailwind: —

What you want: Fetch from API → React: useEffect + fetch → Tailwind: —

What you want: Mobile first → React: — → Tailwind: Start with unprefixed, add sm: md: lg:

What you want: Reuse logic → React: Custom hook (useSomething) → Tailwind: @apply (but avoid it)

What you want: Deploy → React: npm run build then upload → Tailwind: —`,
    date: "2024-01-19",
  },
};